import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import jsQR from 'jsqr';
import { Camera, Loader2, ScanLine, XCircle } from 'lucide-react';
import { extractDeviceLinkCode } from '@/utils/deviceLink';

type ScannerStatus =
  | 'idle'
  | 'requesting'
  | 'scanning'
  | 'unsupported'
  | 'permissionDenied'
  | 'scanError'
  | 'invalidQr';

interface DeviceLinkQrScannerProps {
  onCodeScanned: (code: string) => void;
}

export default function DeviceLinkQrScanner({
  onCodeScanned,
}: DeviceLinkQrScannerProps) {
  const { t } = useTranslation(['auth', 'common']);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const [status, setStatus] = useState<ScannerStatus>('idle');

  const stopScanning = useCallback(() => {
    if (animationFrameRef.current !== null) {
      window.cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  useEffect(() => stopScanning, [stopScanning]);

  const scanFrame = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (
      !video ||
      !canvas ||
      video.readyState < HTMLMediaElement.HAVE_ENOUGH_DATA
    ) {
      animationFrameRef.current = window.requestAnimationFrame(scanFrame);
      return;
    }

    const width = video.videoWidth;
    const height = video.videoHeight;
    if (width === 0 || height === 0) {
      animationFrameRef.current = window.requestAnimationFrame(scanFrame);
      return;
    }

    if (canvas.width !== width) {
      canvas.width = width;
    }
    if (canvas.height !== height) {
      canvas.height = height;
    }

    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) {
      setStatus('scanError');
      stopScanning();
      return;
    }

    context.drawImage(video, 0, 0, width, height);
    const imageData = context.getImageData(0, 0, width, height);
    const qrCode = jsQR(imageData.data, imageData.width, imageData.height, {
      inversionAttempts: 'attemptBoth',
    });

    if (qrCode) {
      const code = extractDeviceLinkCode(qrCode.data);
      if (code) {
        stopScanning();
        onCodeScanned(code);
        return;
      }
      setStatus('invalidQr');
    }

    animationFrameRef.current = window.requestAnimationFrame(scanFrame);
  };

  const startScanning = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus('unsupported');
      return;
    }

    stopScanning();
    setStatus('requesting');

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });

      streamRef.current = stream;

      if (!videoRef.current) {
        stopScanning();
        return;
      }

      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      setStatus('scanning');
      animationFrameRef.current = window.requestAnimationFrame(scanFrame);
    } catch (error) {
      stopScanning();
      const errorName =
        error instanceof DOMException ? error.name : 'UnknownError';
      setStatus(
        errorName === 'NotAllowedError' || errorName === 'PermissionDeniedError'
          ? 'permissionDenied'
          : 'scanError'
      );
    }
  };

  const isStarting = status === 'requesting';
  const isScanning = status === 'scanning' || status === 'invalidQr';
  const isCameraPreviewVisible = isStarting || isScanning;

  return (
    <div className="space-y-4 text-left">
      <div className="overflow-hidden rounded-lg border border-secondary-200 bg-secondary-950">
        <div className="relative aspect-[4/3] w-full">
          <video
            ref={videoRef}
            className={`absolute inset-0 h-full w-full object-cover ${isCameraPreviewVisible ? '' : 'hidden'}`}
            muted
            playsInline
            aria-label={t('auth:linkDeviceScannerVideo')}
          />
          {isScanning ? (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="h-44 w-44 rounded-lg border-2 border-white/80 shadow-[0_0_0_9999px_rgba(2,6,23,0.38)]" />
            </div>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center text-secondary-100">
              {isStarting ? (
                <Loader2 className="h-9 w-9 animate-spin" />
              ) : (
                <Camera className="h-9 w-9" />
              )}
              <p className="text-sm leading-6">
                {isStarting
                  ? t('auth:linkDeviceScannerRequesting')
                  : t('auth:linkDeviceScannerIdle')}
              </p>
            </div>
          )}
        </div>
      </div>

      <canvas ref={canvasRef} className="hidden" />

      <div aria-live="polite" className="min-h-[1.5rem] text-sm">
        {status === 'unsupported' && (
          <p className="flex items-start gap-2 text-amber-700">
            <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{t('auth:linkDeviceScannerUnsupported')}</span>
          </p>
        )}
        {status === 'permissionDenied' && (
          <p className="flex items-start gap-2 text-amber-700">
            <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{t('auth:linkDeviceScannerPermissionDenied')}</span>
          </p>
        )}
        {status === 'scanError' && (
          <p className="flex items-start gap-2 text-red-700">
            <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{t('auth:linkDeviceScannerError')}</span>
          </p>
        )}
        {status === 'invalidQr' && (
          <p className="flex items-start gap-2 text-amber-700">
            <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{t('auth:linkDeviceScannerInvalidQr')}</span>
          </p>
        )}
        {status === 'scanning' && (
          <p className="text-secondary-600">
            {t('auth:linkDeviceScannerActive')}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <button
          type="button"
          onClick={startScanning}
          disabled={isStarting}
          className="btn-primary w-full px-4 py-2.5"
        >
          {isStarting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('auth:linkDeviceScannerRequesting')}
            </>
          ) : (
            <>
              <ScanLine className="mr-2 h-4 w-4" />
              {isScanning
                ? t('auth:linkDeviceScannerRestart')
                : t('auth:linkDeviceScannerStart')}
            </>
          )}
        </button>
        {isScanning && (
          <button
            type="button"
            onClick={() => {
              stopScanning();
              setStatus('idle');
            }}
            className="btn-secondary w-full px-4 py-2.5"
          >
            {t('common:cancel')}
          </button>
        )}
      </div>
    </div>
  );
}
